import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Gamepad2, Sparkles, Brain, BookOpenCheck } from "lucide-react";
import { IMAGES } from "../../config/landingConfig";
import { scrollToId } from "../../lib/scroll";
import { PhotoSlot } from "./PhotoSlot";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const BADGES = [
  { icon: BookOpenCheck, label: "Metodyka", color: "bg-cyan text-ink" },
  { icon: Brain, label: "Sztuczna inteligencja", color: "bg-magenta text-white" },
  { icon: Gamepad2, label: "Gry na speaking", color: "bg-violet text-white" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const tilesY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const go = (e, href) => {
    e.preventDefault();
    scrollToId(href);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      ref={ref}
      className="dot-grid relative overflow-hidden border-b-4 border-ink bg-white"
    >
      <motion.div style={{ y: tilesY }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="float-soft absolute left-[4%] top-[16%] hidden h-16 w-16 rounded-2xl border-2 border-ink bg-cyan md:block" style={{ "--rot": "-8deg" }} />
        <div className="float-soft absolute bottom-[10%] left-[3%] hidden h-10 w-10 rounded-xl border-2 border-ink bg-magenta lg:block" style={{ "--rot": "10deg", animationDelay: "0.8s" }} />
        <div className="float-soft absolute right-[6%] top-[10%] hidden h-12 w-12 rounded-full border-2 border-ink bg-violet md:block" style={{ animationDelay: "1.4s" }} />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pb-24 lg:pt-20">
        <div>
          <MaskedLine delay={0.05}>
            <span
              data-testid="hero-eyebrow"
              className="inline-block rounded-full border-2 border-ink bg-white px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-ink shadow-hard-sm sm:text-xs"
            >
              Nowy kurs online dla nauczycieli języków obcych
            </span>
          </MaskedLine>

          <h1 className="font-display mt-6 text-4xl uppercase leading-[0.95] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>AI Speaking</MaskedLine>
            <MaskedLine delay={0.27}>Games</MaskedLine>
          </h1>
          <p className="font-display mt-3 text-2xl uppercase leading-[1.05] tracking-tight text-ink sm:text-3xl lg:text-4xl">
            <MaskedLine delay={0.4}>Gry, które</MaskedLine>
            <MaskedLine delay={0.52}>
              <span className="marker-cyan">naprawdę uczą mówić</span>
            </MaskedLine>
          </p>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.62 }}
            className="mt-6 max-w-xl text-base font-semibold leading-relaxed text-ink sm:text-lg"
          >
            Twórz z pomocą AI gry na speaking, które dają uczniom powód do
            rozmowy - dopasowane do celu lekcji, poziomu i potrzeb Twoich uczniów.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.72 }}
            className="mt-2 max-w-xl text-sm leading-relaxed text-ink/70 sm:text-base"
          >
            Od celu językowego, przez dobry prompt, aż po gotowe karty, plansze i gry interaktywne.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.78 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#pakiety"
              data-testid="hero-cta-primary"
              onClick={(e) => {
                window.fbq?.("trackCustom", "ClickCTA", { location: "hero" });
                go(e, "#pakiety");
              }}
              className="hover-lift rounded-full border-2 border-ink bg-magenta px-7 py-3.5 text-base font-extrabold text-white shadow-hard"
            >
              Wybieram swój pakiet
            </a>
            <a
              href="#program"
              data-testid="hero-cta-secondary"
              onClick={(e) => go(e, "#program")}
              className="hover-lift inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white px-7 py-3.5 text-base font-extrabold text-ink shadow-hard"
            >
              Zobacz program kursu
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.9 }}
            className="mt-8 flex flex-wrap gap-3"
            aria-label="Wyróżniki kursu"
          >
            {BADGES.map(({ icon: Icon, label, color }) => (
              <li
                key={label}
                data-testid={`hero-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-2 rounded-full border-2 border-ink px-4 py-2 text-xs font-extrabold uppercase tracking-wide shadow-hard-sm ${color}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </li>
            ))}
          </motion.ul>

        </div>

        <motion.div
          style={{ y: photoY }}
          initial={{ opacity: 0, scale: 0.94, rotate: 4 }}
          animate={{ opacity: 1, scale: 1, rotate: 2 }}
          transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="absolute -left-4 -top-6 z-10 -rotate-6" aria-hidden="true">
            <span className="font-display inline-block rounded-full border-2 border-ink bg-cyan px-6 py-3 text-base uppercase tracking-wide text-ink shadow-hard sm:text-lg">
              Twórz gry na speaking z AI
            </span>
          </div>

          <PhotoSlot
            src={IMAGES.hero}
            alt="Anna Popławska - prowadząca kurs AI Speaking Games"
            slotLabel="hero"
            className="aspect-[4/5] w-full rounded-[2rem] border-4 border-ink bg-white shadow-hard-lg"
            imgClassName="object-center"
          />

          <div
            data-testid="hero-progress-card"
            className="absolute -bottom-6 -left-3 w-56 -rotate-3 rounded-2xl border-2 border-ink bg-white p-3.5 shadow-hard sm:-left-8"
          >
            <div className="flex items-center justify-between text-[11px] font-extrabold uppercase tracking-wide text-ink">
              <span className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-magenta" aria-hidden="true" />
                Tworzenie gry
              </span>
              <span>lvl&nbsp;5</span>
            </div>
            <div className="mt-2 h-3 overflow-hidden rounded-full border-2 border-ink bg-white">
              <div className="progress-fill h-full rounded-full bg-cyan" />
            </div>
            <p className="mt-2 text-[11px] font-semibold text-ink/60">
              Cel → prompt → test → gotowa gra
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
